import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, MapPin, Plane, TrainFront, Star, MessageSquare, KeyRound, CheckCircle2,
  Navigation, Users, Locate, Volume2, VolumeX, Phone, ArrowUp, ArrowLeft, ArrowRight, Flag,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import Avatar from "@/components/Avatar";
import Button from "@/components/Button";
import { Spinner } from "@/components/States";
import { isPhone } from "@/lib/device";
import RatingStars from "@/components/RatingStars";
import { api } from "@/lib/api";
import { money, miles } from "@/lib/format";
import { speak, cancelVoice } from "@/lib/voice";

const NEXT = {
  accepted: { label: "I've arrived at pickup", status: "arrived" },
  driver_enroute: { label: "I've arrived at pickup", status: "arrived" },
  arrived: { label: "Start trip", status: "in_progress" },
  in_progress: { label: "Complete trip", status: "completed" },
};
const STATUS_TEXT = {
  accepted: "Head to pickup", driver_enroute: "Head to pickup",
  arrived: "Waiting for rider", in_progress: "Trip in progress",
};

function Arrow({ text }) {
  if (text === "🏁") return <Flag className="h-6 w-6 text-white" />;
  if (text === "←") return <ArrowLeft className="h-6 w-6 text-white" />;
  if (text === "→") return <ArrowRight className="h-6 w-6 text-white" />;
  return <ArrowUp className="h-6 w-6 text-white" />;
}

export default function DriverTrip() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [done, setDone] = useState(null);
  const [pin, setPin] = useState("");
  const [pinModal, setPinModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [myPos, setMyPos] = useState(null);
  const [navInfo, setNavInfo] = useState(null);
  const [navStep, setNavStep] = useState(null);
  const [voiceOn, setVoiceOn] = useState(true);
  const [recenter, setRecenter] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const lastSpokenRef = useRef("");
  const doneRef = useRef(false);
  const [riderStars, setRiderStars] = useState(0);
  const [riderRated, setRiderRated] = useState(false);
  const pushRef = useRef({ myPos: null, eta: null, active: false });

  const SHEET_COLLAPSE = 240;

  // Live device position for follow-cam + distance locks.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    const wid = navigator.geolocation.watchPosition(
      (p) => setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: true, maximumAge: 4000, timeout: 15000 }
    );
    return () => { try { navigator.geolocation.clearWatch(wid); } catch { /* noop */ } };
  }, []);

  const poll = useCallback(async () => {
    if (doneRef.current) return;
    try { const r = await api(`/rides/${id}`); setRide(r.ride); } catch { /* noop */ }
  }, [id]);
  useEffect(() => { poll(); const iv = setInterval(poll, 2500); return () => clearInterval(iv); }, [poll]);

  // Push live location + ETA so the rider sees the driver's real position and matching dropoff ETA.
  useEffect(() => {
    const st = ride?.status;
    pushRef.current = {
      myPos,
      eta: navInfo?.durationMin ?? null,
      active: st === "accepted" || st === "driver_enroute" || st === "in_progress",
    };
  }, [myPos, navInfo?.durationMin, ride?.status]);
  useEffect(() => {
    const send = () => {
      const p = pushRef.current;
      if (!p.active || !p.myPos || p.eta == null) return;
      api(`/rides/${id}/driver-location`, { method: "POST", body: { lat: p.myPos.lat, lng: p.myPos.lng, eta_minutes: p.eta } }).catch(() => {});
    };
    send();
    const iv = setInterval(send, 4000);
    return () => clearInterval(iv);
  }, [id]);

  // Voice guidance (turn-by-turn)
  useEffect(() => {
    if (!voiceOn || !navStep?.instruction) return;
    const near = navStep.distanceM != null && navStep.distanceM < 60;
    const key = navStep.instruction + (near ? "|now" : "|far");
    if (key === lastSpokenRef.current) return;
    lastSpokenRef.current = key;
    const text = near
      ? `${navStep.instruction} now.`
      : `In ${navStep.distanceText}, ${navStep.instruction}`;
    speak(text, { force: true });
  }, [navStep, voiceOn]);
  useEffect(() => () => cancelVoice(), []);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const body = { status };
      if (status === "in_progress") body.pin = pin.trim();
      const r = await api(`/rides/${id}/driver-status`, { method: "POST", body });
      if (status === "completed") { doneRef.current = true; setDone(r.ride); try { window.speechSynthesis?.cancel(); } catch { /* noop */ } }
      else { setRide(r.ride); setPinModal(false); }
    } catch (e) { toast.error(e?.message || "Could not update status."); }
    finally { setBusy(false); }
  };
  const cancelTrip = async () => {
    if (!window.confirm("Cancel this trip?")) return;
    try { await api(`/rides/${id}/cancel`, { method: "POST" }); toast.info("Trip cancelled"); navigate("/driver/rides"); }
    catch (e) { toast.error(e?.message || "Failed"); }
  };

  if (!ride && !done) {
    return <div className="flex h-screen items-center justify-center bg-background"><Spinner className="h-8 w-8 text-brand-600" /></div>;
  }

  if (done) {
    const rateRider = async () => {
      if (!riderStars) return;
      try {
        await api(`/rides/${id}/rate`, { method: "POST", body: { rating: riderStars } });
        setRiderRated(true);
        toast.success("Thanks for rating your rider!");
      } catch (e) { toast.error(e?.message || "Could not submit rating."); }
    };
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 overflow-y-auto bg-background px-6 py-10 text-center">
        <CheckCircle2 className="h-16 w-16 text-success" />
        <h2 className="text-2xl font-extrabold text-foreground">Trip completed</h2>
        <p className="font-num text-3xl font-bold text-success">+{money((done.final_fare || 0) + (done.tip || 0))}</p>
        <p className="text-sm text-muted-foreground">{done.pickup.label} → {done.destination.label}</p>

        <div className="mt-5 w-full max-w-sm rounded-2xl border border-border bg-card p-5">
          {riderRated ? (
            <p className="flex items-center justify-center gap-2 font-semibold text-success"><CheckCircle2 className="h-5 w-5" /> Rating submitted</p>
          ) : (
            <>
              <p className="mb-3 font-bold text-foreground">Rate your rider</p>
              <div className="mb-4 flex justify-center"><RatingStars value={riderStars} onChange={setRiderStars} /></div>
              <Button className="w-full" onClick={rateRider} disabled={!riderStars} data-testid="rate-rider-submit">Submit rating</Button>
            </>
          )}
        </div>

        <Button variant="outline" className="mt-4 w-full max-w-sm" onClick={() => navigate("/driver")} data-testid="back-driving">Back to driving</Button>
      </div>
    );
  }

  const s = ride.status;
  const step = NEXT[s];
  const navActive = s === "accepted" || s === "driver_enroute" || s === "in_progress";
  const navFrom = navActive ? (myPos || ride.assigned_driver?.start || ride.pickup) : null;
  const navTo = !navActive ? null : (s === "in_progress" ? ride.destination : ride.pickup);
  const hasGPS = !!myPos;
  const rem = navInfo?.remainingM;
  const arriveLocked = (s === "accepted" || s === "driver_enroute") && hasGPS && rem != null && rem > 150;
  const completeLocked = s === "in_progress" && hasGPS && rem != null && rem > 1609;
  // Starting a trip must happen from a phone (safety + reliable live GPS).
  const startBlocked = step?.status === "in_progress" && !isPhone();

  return (
    <div className="relative h-screen w-full overflow-hidden bg-background">
      {navActive ? (
        <MapView navFrom={navFrom} navTo={navTo} follow recenterKey={recenter} pulsePickup={s !== "in_progress"}
          onRouteInfo={setNavInfo} onNavStep={setNavStep} className="h-full" />
      ) : (
        <MapView pickup={ride.pickup} destination={ride.destination} stops={ride.stops}
          driver={s === "arrived" ? (myPos || ride.pickup) : undefined} showRoute className="h-full" />
      )}

      {/* Back (top-left only when not navigating) */}
      {!navActive && (
        <button onClick={() => navigate("/driver/rides")} data-testid="dt-back"
          className="absolute left-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-card text-foreground shadow-card">
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}

      {/* Nav banner */}
      {navActive && navStep && (
        <div data-testid="nav-banner" className="absolute left-4 right-4 top-4 z-20 flex items-center gap-4 rounded-2xl bg-black px-5 py-4 shadow-card">
          <div className="flex min-w-[58px] flex-col items-center">
            <Arrow text={navStep.arrow} />
            <span className="font-num text-sm font-bold text-white">{navStep.distanceText}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold text-white">{navStep.instruction}</p>
            {navInfo && (
              <p className="truncate text-sm text-white/85">
                {s === "in_progress" ? "To destination" : "To pickup"} · {navInfo.distanceText} · {navInfo.durationText} · Arrive {navInfo.arrivalText}
              </p>
            )}
            {ride.airport_info?.airline && (
              <p className="truncate text-sm font-semibold text-white">✈ {ride.airport_info.airline}{ride.airport_info.flight_number ? ` · ${ride.airport_info.flight_number}` : ""}{ride.airport_info.baggage_claim ? ` · Bag claim ${ride.airport_info.baggage_claim}` : ""}</p>
            )}
          </div>
        </div>
      )}

      {/* Recenter + voice + back (right column during nav) */}
      {navActive && (
        <div className="absolute right-4 top-32 z-30 flex flex-col gap-2">
          <button onClick={() => setRecenter((k) => k + 1)} data-testid="recenter-btn" className="flex h-12 w-12 items-center justify-center rounded-full bg-card shadow-card">
            <Locate className="h-5 w-5 text-brand-600" />
          </button>
          <button onClick={() => setVoiceOn((v) => { if (v) window.speechSynthesis?.cancel(); return !v; })} data-testid="voice-btn"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-card shadow-card">
            {voiceOn ? <Volume2 className="h-5 w-5 text-brand-600" /> : <VolumeX className="h-5 w-5 text-muted-foreground" />}
          </button>
          <button onClick={() => navigate("/driver/rides")} data-testid="dt-back" className="flex h-12 w-12 items-center justify-center rounded-full bg-card shadow-card">
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>
        </div>
      )}

      {/* Bottom sheet */}
      <div style={{ transform: `translateY(${collapsed ? SHEET_COLLAPSE : 0}px)`, transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1)", willChange: "transform" }}
        className="absolute inset-x-0 bottom-0 z-20 rounded-t-3xl border-t border-border bg-card px-5 pb-5 pt-3 shadow-card md:inset-x-auto md:right-4 md:bottom-4 md:w-[420px] md:rounded-3xl md:border">
        <button onClick={() => setCollapsed((c) => !c)} data-testid="dt-sheet-toggle"
          className="absolute -top-11 right-4 flex h-9 items-center gap-1.5 rounded-full bg-card px-3 text-sm font-semibold text-foreground shadow-card">
          {collapsed ? <><ChevronUp className="h-4 w-4" /> Expand</> : <><ChevronDown className="h-4 w-4" /> Collapse</>}
        </button>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-lg font-bold text-foreground">{STATUS_TEXT[s] || s}</span>
          <div className="text-right">
            <p className="font-num text-2xl font-extrabold text-foreground" data-testid="dt-eta">{navInfo?.durationText || "—"}</p>
            {navInfo?.arrivalText && <p className="text-xs text-muted-foreground">Arrive {navInfo.arrivalText}</p>}
          </div>
        </div>

        {s === "arrived" && (
          <div data-testid="dt-meet-rider" className="mb-3 flex items-center gap-3 rounded-2xl bg-secondary p-3">
            <Avatar name={ride.customer_name} size={40} />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Meet your rider</p>
              <p className="font-bold text-foreground">{ride.customer_name}</p>
            </div>
            <button onClick={() => navigate(`/chat/${id}`)} data-testid="dt-meet-chat" className="flex h-10 items-center gap-1.5 rounded-xl bg-brand-600 px-3 text-sm font-bold text-white">
              <MessageSquare className="h-4 w-4" /> Chat
            </button>
          </div>
        )}

        {/* Action */}
        {step && (
          <Button className="w-full" size="lg" loading={busy} disabled={arriveLocked || completeLocked || startBlocked}
            onClick={() => step.status === "in_progress" ? setPinModal(true) : setStatus(step.status)}
            data-testid="advance-status">
            {step.status === "arrived" && <Navigation className="h-5 w-5" />}
            {step.status === "completed" && <CheckCircle2 className="h-5 w-5" />}
            {step.label}
          </Button>
        )}
        {startBlocked && (
          <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs font-medium text-muted-foreground" data-testid="start-phone-only">
            <Phone className="h-3.5 w-3.5" /> For safety, start and drive this trip from the Getaride app on your phone.
          </p>
        )}
        {arriveLocked && <p className="mt-2 text-center text-xs text-muted-foreground">Arrival unlocks within ~500 ft of pickup · {navInfo?.distanceText} away</p>}
        {completeLocked && <p className="mt-2 text-center text-xs text-muted-foreground">Complete unlocks within 1 mile of drop-off · {navInfo?.distanceText} left</p>}

        {/* Rider + route */}
        <div className="mt-4 flex items-center gap-3 border-t border-border pt-4">
          <Avatar name={ride.customer_name} size={44} />
          <div className="flex-1">
            <p className="font-bold text-foreground">{ride.customer_name}</p>
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {Number(ride.customer_rating || 5).toFixed(1)}</span>
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ride.passengers ?? 1}</span>
              <span>{miles(ride.distance_miles)}</span>
            </p>
          </div>
          {s !== "in_progress" && (
            <button onClick={() => navigate(`/chat/${id}`)} data-testid="dt-chat" className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-brand-600"><MessageSquare className="h-5 w-5" /></button>
          )}
          {s !== "in_progress" && (
            <a href="tel:+14070000000" data-testid="dt-call" className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-brand-600"><Phone className="h-5 w-5" /></a>
          )}
        </div>
        <div className="mt-3 rounded-2xl bg-secondary p-3">
          <p className="flex items-center gap-1.5 truncate text-sm text-foreground">
            {ride.pickup.airport ? <Plane className="h-4 w-4 text-brand-600" /> : ride.pickup.station ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-success" />}
            <span className="truncate">{ride.pickup.label}</span>
          </p>
          <div className="my-1 ml-2 h-3 border-l-2 border-dashed border-border" />
          <p className="flex items-center gap-1.5 truncate text-sm text-foreground">
            {ride.destination.airport ? <Plane className="h-4 w-4 text-brand-600" /> : ride.destination.station ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-brand-600" />}
            <span className="truncate">{ride.destination.label}</span>
          </p>
          {ride.airport_info?.airline && (
            <p className="mt-2 flex items-center gap-1.5 border-t border-border pt-2 text-xs font-semibold text-brand-600">
              <Plane className="h-3.5 w-3.5" /> {ride.airport_info.airline}
              {ride.airport_info.flight_number ? ` · ${ride.airport_info.flight_number}` : ""}
              {ride.airport_info.baggage_claim ? ` · Bag claim ${ride.airport_info.baggage_claim}` : ""}
            </p>
          )}
        </div>

        {(s === "accepted" || s === "driver_enroute" || s === "arrived") && (
          <button onClick={cancelTrip} data-testid="dt-cancel" className="mt-3 w-full text-center text-sm font-semibold text-destructive">Cancel trip</button>
        )}
      </div>

      {/* PIN modal */}
      {pinModal && (
        <div className="absolute inset-0 z-40 flex items-end justify-center bg-black/50 md:items-center" onClick={() => setPinModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-t-3xl bg-card p-6 pb-8 shadow-card sm:rounded-3xl">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-accent"><KeyRound className="h-7 w-7 text-brand-600" /></div>
            <h3 className="text-center text-xl font-extrabold text-foreground">Enter start PIN</h3>
            <p className="mb-4 mt-1 text-center text-sm text-muted-foreground">Ask your rider for their 4-digit PIN to start the trip.</p>
            <input data-testid="pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="0000"
              className="mb-3 h-14 w-full rounded-xl border border-border bg-secondary text-center font-num text-2xl font-bold tracking-[0.5em] text-foreground outline-none focus:border-brand-500" />
            {ride.start_pin && <p className="mb-2 text-center text-xs text-muted-foreground">Rider&apos;s PIN: {ride.start_pin}</p>}
            <Button className="w-full" size="lg" loading={busy} disabled={pin.length !== 4} onClick={() => setStatus("in_progress")} data-testid="start-trip">Start trip</Button>
          </div>
        </div>
      )}
    </div>
  );
}
